import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Grid, IconButton, Collapse } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import DeleteIcon from '@material-ui/icons/Delete'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import SydLabel from '../../../../../../../../commonDesign/SydLabel'
import SydSelect from '../../../../../../../../commonDesign/SydSelect'
import SydInput from '../../../../../../../../commonDesign/SydInput'
import SydButton from '../../../../../../../../commonDesign/Button'
import Switch from '../../../../../../../../molecules/Switch'
import DragAndDropListItem from '../../../../../../../../organisms/GroupingProvider/DragAndDropListItem'
import JsonTextarea from './JsonTextarea'

const seriesTypeOptions = [
  { label: 'Bar', value: 'bar' },
  { label: 'Line', value: 'line' },
  { label: 'Scatter', value: 'scatter' }
]

const stepOptions = [
  { label: 'None', value: 'none' },
  { label: 'Start', value: 'start' },
  { label: 'Middle', value: 'middle' },
  { label: 'End', value: 'end' }
]

const scalingModeOptions = [
  { label: 'Linear', value: 'linear' },
  { label: 'Logarithmic', value: 'log' },
  { label: 'Exponential', value: 'exp' },
  { label: 'Outlier (z-score)', value: 'outlier' }
]

const TYPE_ICONS = {
  bar: '\u2587',
  line: '\u2500',
  scatter: '\u25CF'
}

const useStyles = makeStyles(() => ({
  seriesCard: {
    border: '1px solid var(--border-color)',
    borderRadius: 'var(--border-radius-sm)',
    marginBottom: '8px',
    overflow: 'hidden'
  },
  seriesHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: 'var(--color-neutral-50, #fafafa)'
    }
  },
  typeIcon: {
    fontSize: '1rem',
    width: '20px',
    textAlign: 'center',
    color: 'var(--color-primary-500)'
  },
  seriesName: {
    flex: 1,
    fontWeight: 500,
    fontSize: '0.875rem'
  },
  seriesMeta: {
    fontSize: '0.75rem',
    color: 'var(--text-color-subtle)'
  },
  seriesBody: {
    padding: '12px',
    borderTop: '1px solid var(--border-color)'
  }
}))

function generateSeriesId () {
  return `s${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`
}

function SeriesListBuilder ({ series, columns, yAxes, allPurposes, onChange }) {
  const classes = useStyles()
  const [expandedIds, setExpandedIds] = React.useState({})

  const columnOptions = useMemo(() => {
    return [
      { label: 'Select a column...', value: '__placeholder__' },
      ...columns.map(col => ({ label: col.name, value: col.name }))
    ]
  }, [columns])

  const yAxisOptions = useMemo(() => {
    return yAxes.map((y, idx) => ({
      label: `${y.axisId} (${y.position || 'left'})`,
      value: String(idx)
    }))
  }, [yAxes])

  const purposeOverrideOptions = useMemo(() => {
    const purposes = Object.entries(allPurposes || {})
    return [
      { label: '(Use chart purpose)', value: '__chart__' },
      ...purposes.map(([code, data]) => ({
        label: `${data.displayName} (${code})`,
        value: code
      }))
    ]
  }, [allPurposes])

  const toggleExpanded = useCallback((seriesId) => {
    setExpandedIds(prev => ({ ...prev, [seriesId]: !prev[seriesId] }))
  }, [])

  const handleSeriesFieldChange = useCallback((idx, field, value) => {
    const updated = [...series]
    updated[idx] = { ...updated[idx], [field]: value }
    onChange(updated)
  }, [series, onChange])

  const handleSeriesFieldsChange = useCallback((idx, fields) => {
    const updated = [...series]
    updated[idx] = { ...updated[idx], ...fields }
    onChange(updated)
  }, [series, onChange])

  const handleAddSeries = useCallback(() => {
    const id = generateSeriesId()
    const newSeries = {
      seriesId: id,
      type: 'bar',
      name: `Series ${series.length + 1}`,
      purposeCode: null,
      yAccessor: null,
      yAxisIndex: 0,
      color: null,
      stack: null,
      barWidth: null,
      smooth: false,
      areaStyle: false,
      step: null,
      sizeAccessor: null,
      symbolSize: null,
      sizeMin: null,
      sizeMax: null
    }
    onChange([...series, newSeries])
    setExpandedIds(prev => ({ ...prev, [id]: true }))
  }, [series, onChange])

  const handleRemoveSeries = useCallback((idx, e) => {
    e.stopPropagation()
    onChange(series.filter((_, i) => i !== idx))
  }, [series, onChange])

  const handleReorder = useCallback((dragIndex, hoverIndex) => {
    const updated = [...series]
    const [moved] = updated.splice(dragIndex, 1)
    updated.splice(hoverIndex, 0, moved)
    onChange(updated)
  }, [series, onChange])

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
        <h4 style={{ margin: 0 }}>Series</h4>
        <SydButton size='sm' variant='primary' onClick={handleAddSeries}>
          + Add Series
        </SydButton>
      </div>

      {series.length === 0 && (
        <div style={{ color: 'var(--text-color-subtle)', fontSize: '0.875rem', padding: '16px 0' }}>
          No series configured. Add a series to define chart data.
        </div>
      )}

      <DndProvider backend={HTML5Backend}>
        {series.map((s, idx) => (
          <DragAndDropListItem
            key={s.seriesId}
            index={idx}
            onMove={handleReorder}
          >
            <div className={classes.seriesCard}>
              <div
                className={classes.seriesHeader}
                onClick={() => toggleExpanded(s.seriesId)}
              >
                <span className={classes.typeIcon}>{TYPE_ICONS[s.type] || '?'}</span>
                <span className={classes.seriesName}>{s.name || s.seriesId}</span>
                <span className={classes.seriesMeta}>{s.type} &bull; {s.yAccessor || 'no column'}</span>
                <IconButton
                  size='small'
                  onClick={(e) => handleRemoveSeries(idx, e)}
                  style={{ color: 'var(--color-danger-500)' }}
                >
                  <DeleteIcon fontSize='small' />
                </IconButton>
                {expandedIds[s.seriesId]
                  ? <ExpandLessIcon fontSize='small' />
                  : <ExpandMoreIcon fontSize='small' />}
              </div>
              <Collapse in={!!expandedIds[s.seriesId]}>
                <div className={classes.seriesBody}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={3}>
                      <SydLabel required label='Name'>
                        <SydInput
                          minWidth='0'
                          value={s.name || ''}
                          onChange={(e) => handleSeriesFieldChange(idx, 'name', e.target.value)}
                        />
                      </SydLabel>
                    </Grid>
                    <Grid item xs={6} md={2}>
                      <SydLabel required label='Type'>
                        <SydSelect
                          minWidth='0'
                          options={seriesTypeOptions}
                          value={s.type || 'bar'}
                          onChange={(e) => handleSeriesFieldChange(idx, 'type', e.target.value)}
                        />
                      </SydLabel>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <SydLabel required label='Y Column'>
                        <SydSelect
                          minWidth='0'
                          options={columnOptions}
                          value={s.yAccessor || '__placeholder__'}
                          onChange={(e) => handleSeriesFieldChange(idx, 'yAccessor', e.target.value === '__placeholder__' ? null : e.target.value)}
                        />
                      </SydLabel>
                    </Grid>
                    <Grid item xs={6} md={2}>
                      <SydLabel label='Y-Axis'>
                        <SydSelect
                          minWidth='0'
                          options={yAxisOptions}
                          value={String(s.yAxisIndex || 0)}
                          onChange={(e) => handleSeriesFieldChange(idx, 'yAxisIndex', Number(e.target.value))}
                        />
                      </SydLabel>
                    </Grid>
                    <Grid item xs={6} md={2}>
                      <SydLabel label='Color Override'>
                        <SydInput
                          minWidth='0'
                          value={s.color || ''}
                          onChange={(e) => handleSeriesFieldChange(idx, 'color', e.target.value || null)}
                          placeholder='Auto'
                        />
                      </SydLabel>
                    </Grid>

                    {/* Purpose override */}
                    <Grid item xs={12} md={4}>
                      <SydLabel label='Purpose Override' helperText='Fetch from a different purpose'>
                        <SydSelect
                          minWidth='0'
                          options={purposeOverrideOptions}
                          value={s.purposeCode || '__chart__'}
                          onChange={(e) => handleSeriesFieldChange(idx, 'purposeCode', e.target.value === '__chart__' ? null : e.target.value)}
                        />
                      </SydLabel>
                    </Grid>

                    {/* Bar-specific fields */}
                    {s.type === 'bar' && (
                      <>
                        <Grid item xs={6} md={4}>
                          <SydLabel label='Stack Group' helperText='Series with same stack group stack together'>
                            <SydInput
                              minWidth='0'
                              value={s.stack || ''}
                              onChange={(e) => handleSeriesFieldChange(idx, 'stack', e.target.value || null)}
                              placeholder='(no stacking)'
                            />
                          </SydLabel>
                        </Grid>
                        <Grid item xs={6} md={4}>
                          <SydLabel label='Bar Width'>
                            <SydInput
                              minWidth='0'
                              type='number'
                              value={s.barWidth || ''}
                              onChange={(e) => handleSeriesFieldChange(idx, 'barWidth', e.target.value ? Number(e.target.value) : null)}
                              placeholder='Auto'
                            />
                          </SydLabel>
                        </Grid>
                      </>
                    )}

                    {/* Line-specific fields */}
                    {s.type === 'line' && (
                      <>
                        <Grid item xs={4} md={2}>
                          <SydLabel label='Smooth'>
                            <Switch
                              checked={!!s.smooth}
                              onChange={(e) => handleSeriesFieldChange(idx, 'smooth', e.target.checked)}
                            />
                          </SydLabel>
                        </Grid>
                        <Grid item xs={4} md={2}>
                          <SydLabel label='Area Fill'>
                            <Switch
                              checked={!!s.areaStyle}
                              onChange={(e) => handleSeriesFieldChange(idx, 'areaStyle', e.target.checked)}
                            />
                          </SydLabel>
                        </Grid>
                        <Grid item xs={4} md={4}>
                          <SydLabel label='Step'>
                            <SydSelect
                              minWidth='0'
                              options={stepOptions}
                              value={s.step || 'none'}
                              onChange={(e) => handleSeriesFieldChange(idx, 'step', e.target.value === 'none' ? null : e.target.value)}
                            />
                          </SydLabel>
                        </Grid>
                      </>
                    )}

                    {/* Scatter-specific fields */}
                    {s.type === 'scatter' && (
                      <>
                        <Grid item xs={6} md={3}>
                          <SydLabel label='Size Column' helperText='Column for bubble sizing'>
                            <SydSelect
                              minWidth='0'
                              options={columnOptions}
                              value={s.sizeAccessor || '__placeholder__'}
                              onChange={(e) => {
                                const val = e.target.value === '__placeholder__' ? null : e.target.value
                                if (val) {
                                  handleSeriesFieldsChange(idx, {
                                    sizeAccessor: val,
                                    ...(typeof s.symbolSize !== 'string' ? { symbolSize: 'linear' } : {})
                                  })
                                } else {
                                  handleSeriesFieldsChange(idx, {
                                    sizeAccessor: null,
                                    symbolSize: null,
                                    sizeMin: null,
                                    sizeMax: null
                                  })
                                }
                              }}
                            />
                          </SydLabel>
                        </Grid>
                        {s.sizeAccessor ? (
                          <>
                            <Grid item xs={6} md={3}>
                              <SydLabel label='Scaling Mode' helperText='How data values map to pixel sizes'>
                                <SydSelect
                                  minWidth='0'
                                  options={scalingModeOptions}
                                  value={typeof s.symbolSize === 'string' ? s.symbolSize : 'linear'}
                                  onChange={(e) => handleSeriesFieldChange(idx, 'symbolSize', e.target.value)}
                                />
                              </SydLabel>
                            </Grid>
                            <Grid item xs={6} md={3}>
                              <SydLabel label='Size Min (px)' helperText='Minimum bubble size'>
                                <SydInput
                                  minWidth='0'
                                  type='number'
                                  value={s.sizeMin ?? ''}
                                  onChange={(e) => handleSeriesFieldChange(idx, 'sizeMin', e.target.value ? Number(e.target.value) : null)}
                                  placeholder='5'
                                />
                              </SydLabel>
                            </Grid>
                            <Grid item xs={6} md={3}>
                              <SydLabel label='Size Max (px)' helperText='Maximum bubble size'>
                                <SydInput
                                  minWidth='0'
                                  type='number'
                                  value={s.sizeMax ?? ''}
                                  onChange={(e) => handleSeriesFieldChange(idx, 'sizeMax', e.target.value ? Number(e.target.value) : null)}
                                  placeholder='50'
                                />
                              </SydLabel>
                            </Grid>
                          </>
                        ) : (
                          <Grid item xs={6} md={3}>
                            <SydLabel label='Symbol Size' helperText='Fixed size for all points'>
                              <SydInput
                                minWidth='0'
                                type='number'
                                value={typeof s.symbolSize === 'number' ? s.symbolSize : ''}
                                onChange={(e) => handleSeriesFieldChange(idx, 'symbolSize', e.target.value ? Number(e.target.value) : null)}
                                placeholder='Auto'
                              />
                            </SydLabel>
                          </Grid>
                        )}
                      </>
                    )}

                    {/* Series overrides (escape hatch) */}
                    <Grid item xs={12}>
                      <JsonTextarea
                        label='Series Overrides'
                        helperText='ECharts series options (label, itemStyle, emphasis, etc.) as JSON'
                        value={s.overrides}
                        onChange={(val) => handleSeriesFieldChange(idx, 'overrides', val)}
                        rows={2}
                        fontSize='0.75rem'
                      />
                    </Grid>
                  </Grid>
                </div>
              </Collapse>
            </div>
          </DragAndDropListItem>
        ))}
      </DndProvider>
    </div>
  )
}

SeriesListBuilder.propTypes = {
  series: PropTypes.array.isRequired,
  columns: PropTypes.array.isRequired,
  yAxes: PropTypes.array.isRequired,
  allPurposes: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired
}

export default SeriesListBuilder
